import { useSelect } from '@refinedev/antd';
import { UseSelectProps } from '@refinedev/core';
import { SelectProps } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCountryContext } from '@/contexts/CountryContext';
import useDebouncedValue from '@/hooks/useDebouncedValue';

/**
 * Hook para manejar un select con scroll infinito y búsqueda reactiva.
 * Utiliza `useSelect` de Refine para obtener los datos paginados, acumular opciones y evitar duplicados.
 * No se ocupa de valores iniciales: para eso se puede componer con `useSelectPropsWithInitial`.
 *
 * @param selectData Configuración base del hook `useSelect`.
 * @param options Ajustes adicionales para la búsqueda paginada.
 * @param options.searchField Campo utilizado como filtro de búsqueda en el backend.
 * @param options.pageSize Cantidad de elementos por página.
 * @param options.getOption Función opcional para transformar los datos en opciones.
 * @param options.saveItem Indica si se debe preservar el objeto original en la opción.
 * @returns Propiedades de `Select` listas para usarse junto con `onPopupScroll`.
 */

export const useSelectScrollSearch = (
  selectData: UseSelectProps<any, any, any>,
  options: {
    searchField?: string; // Campo por el cual se realizará la búsqueda
    pageSize?: number; // Número de elementos por página
    getOption?: (data: any) => any; // Función opcional para transformar los datos en opciones
    saveItem?: boolean; // Opcional si se desea que la opción almacene el objeto del que se obtiene
  } = {},
): { selectProps: SelectProps; refetchData: () => void } => {
  const { selectedCountryId } = useCountryContext();
  const [current, setCurrent] = useState(1);
  const [accumulatedOptions, setAccumulatedOptions] = useState<any[]>([]);
  const [rawSearch, setRawSearch] = useState('');
  const search = useDebouncedValue(rawSearch, 300);
  const searchField = useMemo(() => options.searchField || 'search', [options.searchField]);
  const pageSize = useMemo(() => options.pageSize || 20, [options.pageSize]);
  const filterValues = useMemo(
    () => selectData.filters?.map(f => f.value) || [],
    [selectData.filters],
  );
  // Serialización estable para usar como dependencia en useEffect sin hacer spread (evita que el array cambie de tamaño entre renders)
  const filterValuesKey = useMemo(() => JSON.stringify(filterValues), [filterValues]);
  const filters = useMemo(
    () => [
      ...(selectData.filters || []),
      ...(search ? [{ field: searchField, operator: 'eq' as const, value: search }] : []),
    ],
    [selectData.filters, search, searchField],
  );
  const selectDataModified: UseSelectProps<any, any, any> = useMemo(
    () => ({
      ...selectData,
      filters: filters,
      queryOptions: {
        ...selectData.queryOptions,
        staleTime: 0,
        keepPreviousData: true,
        queryKey:
          selectData.queryOptions?.queryKey && Array.isArray(selectData.queryOptions.queryKey)
            ? [
                selectData.resource,
                ...selectData.queryOptions.queryKey,
                ...filterValues,
                search,
                current,
              ]
            : [selectData.resource, ...filterValues, search, current], // ← importante
        retry: false,
      },
      pagination: { pageSize: pageSize, currentPage: current },
      meta: {
        ...(selectData.meta || {}),
        id: undefined,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectData, pageSize, current, filterValues, search],
  );
  const {
    selectProps,
    query: { data, isFetching, isLoading, refetch },
  } = useSelect(selectDataModified);
  const total = data?.total ?? 0;
  // ======= CORRECCIÓN: calculo de maxPage =======
  const maxPage = useMemo(() => (total > 0 ? Math.ceil(total / pageSize) : 1), [total, pageSize]);
  // ==============================================

  useEffect(() => {
    if (data?.data) {
      setAccumulatedOptions(prev => {
        const existingIds = new Set(prev.map(o => o.value));
        const next = data.data.reduce((acc: any[], item: any) => {
          const option = options.getOption
            ? options.getOption(item)
            : {
                label: selectData.optionLabel?.(item),
                value: selectData.optionValue ? selectData.optionValue(item) : item.id,
                item: options.saveItem ? item : undefined,
              };

          if (!existingIds.has(option.value)) {
            acc.push(option);
          }

          return acc;
        }, []);

        return [...prev, ...next];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data, isFetching]);

  // Reset al cambiar el país seleccionado para evitar que opciones de un país anterior persistan en el acumulador.
  useEffect(() => {
    setCurrent(1);
    setAccumulatedOptions([]);
  }, [selectedCountryId]);
  // Reset al cambiar filtros o search
  useEffect(() => {
    setCurrent(1);
    setAccumulatedOptions([]);
  }, [filterValuesKey, search]);
  // Reset de search al cambiar filtros
  useEffect(() => {
    setRawSearch('');
  }, [filterValuesKey]);

  const lockRef = useRef(false);
  // ======= CORRECCIÓN: agrego current < maxPage =======
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const target = e.target as HTMLDivElement;
      const atBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 10;

      if (
        atBottom &&
        !isFetching &&
        total &&
        accumulatedOptions.length < total &&
        !lockRef.current &&
        current < maxPage // <= ESTE ES EL CAMBIO IMPORTANTE
      ) {
        lockRef.current = true;
        setCurrent(prev => prev + 1);
        setTimeout(() => {
          lockRef.current = false;
        }, 300); // el delay puede ser 300-500ms, lo justo para que suba el flag de isFetching
      }
    },
    [accumulatedOptions.length, total, isFetching, current, maxPage],
  );
  // ================================================

  return {
    selectProps: {
      ...selectProps,
      loading: isFetching || isLoading,
      notFoundContent:
        (isFetching || isLoading) && accumulatedOptions.length === 0
          ? 'Cargando...'
          : accumulatedOptions.length === 0
            ? 'No se encontraron resultados'
            : null,
      options: accumulatedOptions,
      onPopupScroll: handleScroll,
      filterOption: false,
      showSearch: true,
      onSelect: (value: any, option) => {
        if (selectProps.onSelect) {
          selectProps.onSelect(value, option);
        }

        // Reset search when an option is selected
        setRawSearch('');
        setCurrent(1);
      },
      onSearch: (value: string) => {
        setRawSearch(value);
        setCurrent(1);
      },
    },
    refetchData: () => {
      setRawSearch('');
      setCurrent(1);
      void refetch();
    },
  };
};
